<template>
  <div id="timeline-container" class="timeline">
    <div class="grid-container">
      <v-skeleton-loader
        v-if="!showTimeline"
        type="image"
        tile
        class="skeleton main"
        :width="`100%`"
        height="133px"
      ></v-skeleton-loader>
      <p v-if="!showTimeline" class="loading-text">Loading Timeline</p>
      <main v-if="showTimeline" class="grid-item main">
        <div class="timeline-overlay">
          <div class="timeline-center"></div>
        </div>
        <div ref="slider" class="items" @mousedown="startDrag">
          <div class="events-view" :style="{ width: `${timelineWidth}px` }">
            <div class="snapshots">
              <div
                class="event-line"
                v-for="cameraEvent in eventsSnaphots"
                :key="`s-${cameraEvent.eventId}`"
                :style="{ left: `calc(${cameraEvent.startPos}%)` }"
              >
                <div class="snapshot" @mousedown="setTargetEvent(cameraEvent)">
                  <v-lazy
                    v-if="!(cameraEvent.type === 'pirDetected' || cameraEvent.type === 'vmdDetected' || cameraEvent.type === 'audioDetected')"
                    :options="{
                      threshold: 0.5
                    }"
                    transition="fade-transition"
                  >
                    <v-img
                      max-height="55px"
                      :src="getEventImg(cameraEvent)"
                    ></v-img>
                  </v-lazy>
                  <v-img
                    v-else
                    class="event-icon mx-auto"
                    contain
                    :src="getEventImg(cameraEvent)"
                  ></v-img>
                  <p class="overlay-text">{{ getTranslatedEventType(cameraEvent) }}</p>
                </div>
              </div>
            </div>
            <div class="event-markers">
              <div
                class="event-marker"
                v-for="cameraEvent in filteredEvents"
                :key="`e-${cameraEvent.eventId}`"
                :style="{ left: `calc(${cameraEvent.startPos}%)` }"
              ></div>
            </div>
          </div>
          <div
            v-if="showTimeline"
            class="progress"
            :style="{ width: `${timelineWidth}px` }"
          >
            <div
              class="recording-marker"
              v-for="recording in filteredRecordings"
              :key="`r-${recording.recordingId}`"
              :style="{
                left: `calc(${recording.startPos}%)`,
                width: `${recording.width}%`
              }"
            ></div>
            <div
              class="chunk-marker"
              v-for="chunk in filteredChunks"
              :key="`chunk-${chunk.timestamp}`"
              :style="{ left: `calc(${chunk.startPos}%)` }"
            ></div>
          </div>
          <div class="item" v-for="ts in timeDivisions" :key="ts.timestamp">
            <div class="small-marker"></div>
            <div>
              <div class="marker"></div>
              <p :class="{ time: true, hidden: !showTimeline }">
                {{ ts.time }}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
    <!-- <v-btn large icon absolute left fab class="jump jump-backward" :ripple="false" @click="backward">
      <v-icon>chevron_left</v-icon>
    </v-btn>
    <v-btn large icon absolute right fab class="jump jump-forward" :ripple="false" @click="forward">
      <v-icon>chevron_right</v-icon>
    </v-btn> -->
  </div>
</template>

<script>
import * as Bowser from "bowser";
import restapi from "../../service/CMApi";
import { pad } from "../../service/main";

export default {
  name: "TimeLine",
  props: [
    "recordings",
    "events",
    "eventTypes",
    "chunks",
    "from",
    "till",
    "timeSpan",
    "date",
    "currentTime",
    "timeLevel",
    "timing",
    "showTimeline",
    "timelineOffset"
  ],
  data() {
    return {
      isScrolling: false,
      selectedRecording: null,
      selectedEvent: null,
      startDragFrom: null,
      startDragTime: false,
      lastEventPosition: 0,
      showChunks: false,
      showNewEvents: false,
      isDown: false,
      startX: null,
      scrollLeft: null,
      timelineProgress: 50,
      timelineWidth: 0,
      dayLimit: 1.5,
      timelineCurrentTime: 0,
      timeFrom: 0,
      timeTill: 0,
      walk: 0,
      timeDivisionTimer: 0,
      filteredRecordings: [],
      filteredEvents: [],
      filteredChunks: [],
      eventsSnaphots: [],
      browser: null,
      prevRecordingsLength: 0,
      prevEventsLength: 0,
      minute: 60000,
      hour: 3600000,
      day: 86400000,
      targetEvent: null,
      timeInterval: null,
      timeGaps: [1000, 10000, 30000, 60000],
      infraredEventImg: require("../../assets/images/infrared_event.svg"),
      soundEventImg: require("../../assets/images/sound_event.svg"),
      motionEventImg: require("../../assets/images/motion_event.svg")
    };
  },
  mounted() {
    let envData = Bowser.parse(window.navigator.userAgent);
    this.browser = envData.browser;
    window.addEventListener("mouseup", this.endDrag);
    window.addEventListener("mousemove", this.drag);
  },
  beforeDestroy() {
    window.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("mousemove", this.drag);
  },
  watch: {
    showTimeline() {
      this.$nextTick(() => {
        this.updateScrollPos();
        this.updateFilteredRecordings();
        this.updateFilteredEvents();
        if (this.showChunks) {
          this.updateFilteredChunks();
        }
        this.timelineWidth = this.$refs.slider
          ? this.$refs.slider.scrollWidth
          : 0;
      });
    },
    chunks() {
      if (this.showChunks) {
        this.updateFilteredChunks();
      }
    },
    events(newValue, oldValue) {
      let length = newValue.length;
      if (length !== this.prevEventsLength) {
        this.prevEventsLength = length;
        this.updateFilteredEvents();
        this.updateFilteredRecordings();
      }
    },
    recordings(newValue, oldValue) {
      this.updateFilteredRecordings();
    },
    currentTime(newValue, oldValue) {
      if (!this.startDragTime) {
        if (!this.showTimeline) {
          this.updateScrollPos();
          this.timelineCurrentTime = newValue - this.timelineOffset;
          this.timeDivisionTimer = newValue - this.timelineOffset;
        } else {
          if (
            Math.abs(newValue - this.timeDivisionTimer) > this.timing * 24 &&
            !this.isScrolling
          ) {
            this.updateScrollPos(false, true);
            this.timeDivisionTimer = newValue - this.timelineOffset;
          } else if (
            Math.abs(newValue - this.timeDivisionTimer) > 10000 &&
            !this.isScrolling
          ) {
            this.updateScrollPos(true, true);
            this.timeDivisionTimer = newValue - this.timelineOffset;
          } else {
            this.updateScrollPos(false, false);
          }
        }
      }
    },
    timeDivisions(newValue, oldValue) {
      this.updateScrollPos(false, false);
      this.updateFilteredRecordings();
      this.updateFilteredEvents();
      this.updateFilteredChunks();
      this.timelineWidth = this.$refs.slider
        ? this.$refs.slider.scrollWidth
        : 0;
    }
  },
  computed: {
    currentTimeLimit() {
      let position;
      switch (this.browser.name) {
        case "Firefox":
          position =
            (this.timeTill - new Date().getTime() + this.timelineOffset) /
            (this.timeTill - this.timeFrom);
          return (
            position * -this.$refs.slider.scrollWidth +
            this.$refs.slider.clientWidth / 2
          );

        case "Safari":
          position =
            (this.timeTill - new Date().getTime() + this.timelineOffset) /
            (this.timeTill - this.timeFrom);
          return (
            position * -this.$refs.slider.scrollWidth +
            this.$refs.slider.clientWidth / 2
          );

        case "Chrome":
          if (Number(this.browser.version.split(".")[0]) > 84) {
            position =
              (this.timeTill - new Date().getTime() + this.timelineOffset) /
              (this.timeTill - this.timeFrom);
            return (
              position * -this.$refs.slider.scrollWidth +
              this.$refs.slider.clientWidth / 2
            );
          } else {
            position =
              (new Date().getTime() + this.timelineOffset - this.timeFrom) /
              (this.timeTill - this.timeFrom);
            return (
              position * this.$refs.slider.scrollWidth -
              this.$refs.slider.clientWidth / 2
            );
          }

        default:
          position =
            (new Date().getTime() + this.timelineOffset - this.timeFrom) /
            (this.timeTill - this.timeFrom);
          return (
            position * this.$refs.slider.scrollWidth -
            this.$refs.slider.clientWidth / 2
          );
      }
    },
    timeDivisions() {
      let t = this.timelineCurrentTime
        ? this.timelineCurrentTime
        : this.date + " 00:00";
      let date = new Date(t);
      if (this.timeLevel === 4) {
        date.setHours(0);
      }
      if (this.timeLevel !== 1) {
        date.setMinutes(0);
      }
      date.setSeconds(0);
      let dt = new Date(date).getTime();

      let time = null;
      let dt2 = null;
      let prev = null;
      let hrs = null;
      let min = null;

      let till = dt + this.dayLimit * this.timing * 24;
      let from = dt - this.dayLimit * this.timing * 24;

      const ts = [];
      for (let i = till; i > from; i -= this.timing) {
        dt2 = new Date(i);
        dt2.setSeconds(0);
        hrs = pad(dt2.getHours());
        min = pad(dt2.getMinutes());
        time = `${hrs}:${min}`;
        if (prev !== time) {
          ts.push({ timestamp: dt2.getTime(), time: time });
          prev = time;
        }
      }

      this.timeTill = till + this.timing / 2;
      this.timeFrom = from + this.timing / 2;

      return ts;
    }
  },
  methods: {
    getEventImg(event) {
      if (event.type === "pirDetected") {
        return this.infraredEventImg;
      } else if (event.type === "vmdDetected") {
        return this.motionEventImg;
      } else if (event.type === "audioDetected") {
        return this.soundEventImg;
      } else {
        return event.snapshot ? event.snapshot : "";
      }
    },
    updateFilteredRecordings() {
      this.filteredRecordings = this.recordings
        .filter(recording => {
          let startTime = new Date(recording.startTime).getTime() - this.timelineOffset
          let endTime = recording.endTime
            ? new Date(recording.endTime).getTime() - this.timelineOffset
            : new Date().getTime() - this.timelineOffset
          return (
            (startTime < this.timeFrom &&
              endTime < this.timeTill &&
              endTime > this.timeFrom) ||
            (startTime > this.timeFrom &&
              startTime < this.timeTill &&
              endTime > this.timeTill) ||
            (endTime < this.timeTill && startTime > this.timeFrom) ||
            (endTime > this.timeTill && startTime < this.timeFrom)
          );
        })
        .map(recording => {
          let startTime = new Date(recording.startTime).getTime() - this.timelineOffset;
          let endTime = recording.endTime
            ? new Date(recording.endTime).getTime() - this.timelineOffset
            : new Date().getTime() - this.timelineOffset
          let startPos;
          let endPos;
          if (endTime > this.timeTill && startTime < this.timeFrom) {
            startPos = 0;
            endPos = 100;
          } else if (endTime > this.timeTill) {
            startPos =
              ((startTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
              100;
            endPos = 100;
          } else if (startTime < this.timeFrom) {
            startPos = 0;
            endPos =
              ((endTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
              100;
          } else {
            startPos =
              ((startTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
              100;
            endPos =
              ((endTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
              100;
          }
          return {
            startPos,
            endPos,
            startTime,
            endTime,
            width: endPos - startPos,
            recordingId: recording.recordingId
          };
        })
        .sort((a, b) => {
          return b.startPos - a.startPos;
        });

      let currentGroup = [];
      let groupedRecordings = [];
      this.filteredRecordings.forEach((recording, index) => {
        currentGroup.push(recording);
        let gap = this.filteredRecordings[index + 1]
          ? recording.startTime - this.filteredRecordings[index + 1].endTime
          : 100000;
        if (gap > this.timeGaps[this.timeLevel - 1]) {
          let startPos = 100;
          let endPos = 0;
          let recordingId = "Group";
          currentGroup.forEach(item => {
            if (item.startPos < startPos) {
              startPos = item.startPos;
            }
            if (item.endPos > endPos) {
              endPos = item.endPos;
            }
            recordingId = `${recordingId}-${item.recordingId}`;
          });
          currentGroup = [];
          groupedRecordings.push({
            startPos,
            endPos,
            width: endPos - startPos,
            recordingId: recordingId
          });
        }
      });
      this.filteredRecordings = groupedRecordings;
    },
    updateFilteredChunks() {
      if (this.showChunks) {
        this.filteredChunks = this.chunks
          .filter(chunk => {
            let time = new Date(chunk.startTime).getTime() - this.timelineOffset;
            return time > this.timeFrom && time < this.timeTill;
          })
          .map(chunk => {
            let startTime = new Date(chunk.startTime).getTime() - this.timelineOffset;
            let startPos =
              ((startTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
              100;
            return {
              startPos,
              timestamp: startTime
            };
          });
      } else {
        this.filteredChunks = [];
      }
    },
    updateFilteredEvents() {
      this.filteredEvents = this.events
        .filter(cameraEvent => {
          let time = new Date(cameraEvent.timestamp).getTime() - this.timelineOffset;
          return (
            time > this.timeFrom + 2 * this.timing &&
            time < this.timeTill - 2 * this.timing
          );
        })
        .map(cameraEvent => {
          let startTime = new Date(cameraEvent.timestamp).getTime() - this.timelineOffset;
          let startPos =
            ((startTime - this.timeFrom) / (this.timeTill - this.timeFrom)) *
            100;
          return { 
            ...cameraEvent, 
            ...{
              startPos,
              timestamp: startTime,
              snapshot: `${restapi.baseURL}/rest/v2.4/events/${cameraEvent.eventId}.jpg?access_token=${restapi.token}`
            }
          };
        });
      this.updateEventsSnaphots();
    },
    updateEventsSnaphots() {
      this.eventsSnaphots = [];
      this.filteredEvents
        .sort((eventA, eventB) => {
          const typeObjectA = this.eventTypes[eventA.type]
          const typeObjectB = this.eventTypes[eventB.type]
          const eventAPriority = typeObjectA ? typeObjectA.priority : 9
          const eventBPriority = typeObjectB ? typeObjectB.priority : 9
          return eventAPriority - eventBPriority;
        })
        .forEach(event => {
          let shouldAdd = true;
          this.eventsSnaphots.forEach(addedEvent => {
            if (Math.abs(addedEvent.startPos - event.startPos) < 1.7) {
              shouldAdd = false;
              return;
            }
          });
          if (shouldAdd) {
            this.eventsSnaphots = [...this.eventsSnaphots, event];
          }
        });
    },
    getTranslatedEventType(event) {
      const eventType = this.eventTypes[event.type]
      if (event.category && eventType.categoriesTranslated && eventType.categoriesTranslated[event.category]) {
        return eventType.categoriesTranslated[event.category]
      } else {
        return eventType ? eventType.eventTypeTranslated : this.$t(event.type)
      }
    },
    smoothScroll(offset, callback = () => {}) {
      const that = this;
      const onScroll = function() {
        if (
          that.$refs.slider.scrollLeft >= offset - 2 &&
          that.$refs.slider.scrollLeft.toFixed() <= offset + 2
        ) {
          that.$refs.slider.removeEventListener("scroll", onScroll);
          that.isScrolling = false;
          callback();
        }
      };

      this.$refs.slider.addEventListener("scroll", onScroll);
      onScroll();
      this.$refs.slider.scrollTo({
        top: 0,
        left: offset,
        behavior: "smooth"
      });
    },
    updateScrollPos(smooth = false, shouldUpdateDivs = false) {
      if (this.$refs.slider && !this.isScrolling) {
        this.isScrolling = true;
        let position;
        let offset;

        switch (this.browser.name) {
          case "Safari":
            this.updatePosition(false, shouldUpdateDivs, true);
            break;

          case "Chrome":
            if (Number(this.browser.version.split(".")[0]) > 84) {
              this.updatePosition(smooth, shouldUpdateDivs, true);
            } else {
              this.updatePosition(smooth, shouldUpdateDivs, false);
            }
            break;

          case "Microsoft Edge":
            if (Number(this.browser.version.split(".")[0]) > 85) {
              this.updatePosition(smooth, shouldUpdateDivs, true);
            } else {
              this.updatePosition(smooth, shouldUpdateDivs, false);
            }
            break;

          default:
            this.updatePosition(smooth, shouldUpdateDivs, true);
            break;
        }
      }
    },
    updatePosition(smooth = false, shouldUpdateDivs = false, inverted = false) {
      let position;
      let offset;

      if (inverted) {
        position =
          (this.timeTill - (this.currentTime - this.timelineOffset)) / (this.timeTill - this.timeFrom);
        offset =
          position * -this.$refs.slider.scrollWidth +
          this.$refs.slider.clientWidth / 2;
        if (smooth) {
          this.smoothScroll(offset, () => {
            if (shouldUpdateDivs) {
              this.timelineCurrentTime = this.currentTime - this.timelineOffset;
            }
          });
        } else {
          this.$refs.slider.scrollLeft = offset;
          this.isScrolling = false;
          if (shouldUpdateDivs) {
            this.timelineCurrentTime = this.currentTime - this.timelineOffset;
          }
        }
      } else {
        position =
          (this.currentTime - this.timelineOffset - this.timeFrom) / (this.timeTill - this.timeFrom);
        offset =
          position * this.$refs.slider.scrollWidth -
          this.$refs.slider.clientWidth / 2;
        if (smooth) {
          this.smoothScroll(offset, () => {
            if (shouldUpdateDivs) {
              this.timelineCurrentTime = this.currentTime - this.timelineOffset;
            }
          });
        } else {
          this.$refs.slider.scrollLeft = offset;
          this.isScrolling = false;
          if (shouldUpdateDivs) {
            this.timelineCurrentTime = this.currentTime - this.timelineOffset;
          }
        }
      }
    },
    startDrag(event) {
      if (this.showTimeline && !this.isScrolling) {
        if (event.shiftKey) {
          this.showChunks = !this.showChunks;
          this.showNewEvents = !this.showNewEvents;
          this.updateFilteredChunks();
          return;
        }
        this.startX =
          event.pageX -
          (document.body.clientWidth - this.$refs.slider.clientWidth);
        this.scrollLeft = this.$refs.slider.scrollLeft;
        this.startDragFrom = this.$refs.slider.clientWidth / 2 - this.startX;
        this.startDragTime = true;

        this.walk = 0;
        this.$emit("dragStart");
        if (this.timeLevel === 1) {
          this.timeInterval = setInterval(() => {
            this.$emit("drag", - (this.walk / 100) * this.timing);
            this.walk = 0;
          }, 500);
        } else {
          this.timeInterval = setInterval(() => {
            if (this.scrollLeft - this.walk < this.currentTimeLimit) {
              this.$emit(
                "dragTime",
                (this.currentTime - this.timelineOffset) - (this.walk / 100) * this.timing
              );
            } else {
              this.$emit("dragTime", new Date().getTime() - this.timelineOffset);
            }
          }, 100);
        }
      }
    },
    drag(event) {
      if (this.startDragTime) {
        const x =
          event.pageX -
          (document.body.clientWidth - this.$refs.slider.clientWidth);
        const walk = x - this.startX;
        this.startX =
          event.pageX -
          (document.body.clientWidth - this.$refs.slider.clientWidth);
        if (
          this.$refs.slider.scrollLeft - this.scrollLeft - walk <
          this.currentTimeLimit - this.scrollLeft
        ) {
          this.$refs.slider.scrollLeft -= walk;
          this.walk += walk;
        } else {
          this.$refs.slider.scrollLeft = this.currentTimeLimit;
        }
      }
    },
    endDrag(event) {
      // this.startX = null
      clearInterval(this.timeInterval);
      this.$emit("dragEnd");
      if (this.startDragTime) {
        if (this.scrollLeft !== this.$refs.slider.scrollLeft) {
          this.$emit(
            "drag",
            -((this.walk ? this.walk : -1) / 100) * this.timing
          );
        } else {
          if (this.targetEvent) {
            this.$emit("eventSelected", this.targetEvent.eventId);
          } else {
            this.$emit("drag", -(this.startDragFrom / 100) * this.timing);
          }
        }

        this.walk = 0;
        this.startDragFrom = 0;
        this.startDragTime = false;
        this.targetEvent = null;
      }
    },
    setTargetEvent(event) {
      this.targetEvent = event;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/main";

.slidecontainer {
  width: 100%;
  .slider {
    -webkit-appearance: none; /* Override default CSS styles */
    appearance: none;
    width: 100%; /* Full-width */
    height: 100%; /* Specified height */
    background: #d3d3d3; /* Grey background */
    outline: none; /* Remove outline */
    opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
    -webkit-transition: 0.2s; /* 0.2 seconds transition on hover */
    transition: opacity 0.2s;
    cursor: grab;
  }
  .slider::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    width: 0px; /* Set a specific slider handle width */
    height: 80px; /* Slider handle height */
    background: transparent; /* Green background */
    cursor: grabbing; /* Cursor on hover */
  }

  .slider::-moz-range-thumb {
    width: 25px; /* Set a specific slider handle width */
    height: 80px; /* Slider handle height */
    background: #4caf50; /* Green background */
    cursor: grabbing; /* Cursor on hover */
  }
}

.timeline {
  display: flex;
  align-items: center;
  margin: 0;
  user-select: none;
  .jump {
    color: $color-general-text;
    background-color: transparent !important;
    &:before,
    &:before,
    &:hover:before {
      background-color: transparent !important;
    }
    i {
      font-size: 64px !important;
    }
    &.jump-backward {
      left: -20px;
    }
    &.jump-forward {
      right: -20px;
    }
  }
}

$gray: #555;
$yellow: #f2e968;
$white: #efefef;

//GET THE GOOGLE FONT SPECIMEN
@import url(https://fonts.googleapis.com/css?family=Rubik);

body,
html {
  color: #fff;
  text-align: center;
  background: $white;
  font-family: Helvetica, sans-serif;
  margin: 0;
}

.grid-container {
  width: 100%;
  position: relative;
  background: $white;
  font-family: "Rubik", sans-serif;
}

//ENABLE CSS GRID FOR LIFT OFF  🚀
@supports (display: grid) {
  .grid-container {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto 1fr auto;

    //Let the craziness begin!!!
    grid-template-areas:
      "header header header"
      "title title footer"
      "main main main";
    @media screen and (max-width: 500px) {
      grid-template-columns: 1fr;
      grid-template-rows: 0.3fr 1fr auto 1fr;
      grid-template-areas:
        "header"
        "title"
        "main"
        "footer";
    }
  }

  .grid-item {
    color: #fff;
    background: skyblue;
    padding: 3.5em 1em;
    font-size: 1em;
    font-weight: 700;
  }

  .header {
    background-color: darken(skyblue, 60%);
    grid-area: header;
    padding: 1em;
  }

  .title {
    color: $gray;
    background-color: lighten(skyblue, 25%);
    grid-area: title;
  }

  .main {
    color: lighten($gray, 25%);
    background-color: lighten(skyblue, 60%);
    grid-area: main;
    padding: 0;
    overflow-x: hidden;
    overflow-y: hidden;
  }

  .footer {
    background-color: darken(skyblue, 10%);
    grid-area: footer;
    padding: 0.6em;
  }

  .timeline-overlay {
    width: 100%;
    height: 0px;
    bottom: 0;
    position: relative;
    pointer-events: none;
    .timeline-center {
      width: 4px;
      height: 64px;
      background-color: $primary;
      position: absolute;
      bottom: -135px;
      left: calc(50% - 2px);
      z-index: 8;
    }
  }

  .items {
    direction: rtl;
    // background-color: #d3d3d3;
    position: relative;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    white-space: nowrap;
    transition: all 0.2s;
    will-change: transform;
    user-select: none;
    cursor: pointer;
  }

  .items.active {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }

  .item {
    display: inline-block;
    background: white;
    min-height: 20px;
    min-width: 100px;
    margin: 0px;
    @media screen and (max-width: 500px) {
      min-height: 200px;
      min-width: 200px;
    }
    .marker {
      width: 2px;
      height: 10px;
      margin: auto;
      background-color: #d3d3d3;
    }
    .small-marker {
      width: 2px;
      height: 5px;
      position: absolute;
      background-color: #d3d3d3;
    }
    .time {
      width: 100%;
      margin-top: -1px;
      margin-bottom: 2px;
      text-align: center;
      text-indent: 0px;
      font-size: 13px;
      &.hidden {
        color: transparent;
      }
    }
  }
}

a {
  display: block;
  color: lighten(skyblue, 15%);
  text-decoration: underline;
  margin: 1em auto;
  &:hover {
    cursor: pointer;
  }
}

p {
  text-align: left;
  text-indent: 20px;
  font-weight: 100;
}

i {
  color: skyblue;
}
.skeleton {
  border-top: 1px solid #d3d3d39f;
  direction: ltr;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  &::v-deep .v-skeleton-loader__bone {
    background-color: white;
    &::after {
      background: linear-gradient(
        90deg,
        transparent,
        rgba(151, 151, 151, 0.3),
        transparent
      ) !important;
    }
  }
}

.loading-text {
  position: absolute;
  left: 50%;
  bottom: 45px;
  transform: translateX(-60%);
}

.events-view {
  height: 86px;
  margin-bottom: 3px;
  border-top: 1px solid #d3d3d39f;
  .snapshots {
    height: 70px;
    direction: ltr;
    position: relative;
    .event-line {
      height: 15px;
      width: 2px;
      margin-left: -1px;
      background-color: $primary;
      bottom: 0px;
      position: absolute;
      margin-bottom: -4px;
      z-index: 1000;
      .snapshot {
        width: 100px;
        height: 55px;
        background-color: #d3d3d3;
        position: relative;
        bottom: 52px;
        border-radius: 4px;
        right: 49px;
        &::v-deep .v-image {
          border-radius: 4px;
        }
        .overlay-text {
          position: absolute;
          top: 0;
          color: white;
          margin: 1px 0 !important;
          padding: 0;
          width: 100%;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-indent: 4px;
          text-shadow: black 0px 1px 5px, black 0px 0px 5px;
          font-size: 11px;
        }
      }
      .event-icon {
        max-width: 30px;
        height: 20px;
        top: 18px;
      }
    }
  }
  .event-markers {
    background-color: #d3d3d3;
    direction: ltr;
    height: 14px;
    position: relative;
    .event-marker {
      height: 75%;
      width: 4px;
      margin-left: -2px;
      background-color: $primary;
      border-radius: 4px;
      bottom: 0px;
      position: absolute;
      margin-bottom: 2px;
    }
  }
}

.progress {
  background-color: #d3d3d3;
  direction: ltr;
  height: 14px;
  position: relative;
  .recording-marker {
    height: 75%;
    background-color: rgb(78, 78, 78);
    border-radius: 4px;
    margin: 0;
    bottom: 0px;
    position: absolute;
    margin-bottom: 2px;
  }
  .chunk-marker {
    height: 75%;
    width: 2px;
    margin-left: -1px;
    background-color: red;
    bottom: 0px;
    position: absolute;
    margin-bottom: 2px;
  }
}
</style>
